import { CAROUSEL_SLIDE_COUNT } from '../config/featured';

// Newest-first among posts carrying a given tag id.
const newestWithTag = (posts, tagId) => {
  if (!tagId) return null;

  const tagged = posts.filter((post) => (post.tags || []).includes(tagId));
  if (!tagged.length) return null;

  return tagged.reduce((newest, post) =>
    new Date(post.date) > new Date(newest.date) ? post : newest
  );
};

/**
 * Decide which posts fill the homepage carousel.
 *
 * Slot 1 is always the most recent post. Slots 2 and 3 are the posts carrying
 * the `featured-1` / `featured-2` tags, in that order regardless of their dates.
 * Duplicates are dropped (a starred post that is also the latest post appears
 * once) and any remaining gap is backfilled with the next most recent posts, so
 * the carousel still fills up before the tags have been created in WordPress.
 *
 * @param {Object[]} recentPosts    Posts from /posts, newest first
 * @param {Object[]} featuredPosts  Posts carrying either featured tag
 * @param {number[]} tagIdsBySlot   [featured-1 tag id, featured-2 tag id]
 * @returns {Object[]} up to CAROUSEL_SLIDE_COUNT raw post objects, in slide order
 */
export const buildCarouselSlides = ({
  recentPosts = [],
  featuredPosts = [],
  tagIdsBySlot = []
} = {}) => {
  const slides = [];
  const usedIds = new Set();

  const add = (post) => {
    if (!post || usedIds.has(post.id) || slides.length >= CAROUSEL_SLIDE_COUNT) return;
    usedIds.add(post.id);
    slides.push(post);
  };

  // Slot 1: latest post
  add(recentPosts[0]);

  // Slots 2..n: starred posts, one per tag, in tag order
  tagIdsBySlot.forEach((tagId) => add(newestWithTag(featuredPosts, tagId)));

  // Backfill anything still empty with the next most recent posts
  recentPosts.forEach(add);

  return slides;
};

export default buildCarouselSlides;
